import type {
  RiskScoreBreakdown,
  RiskScoreBreakdownComplete,
  RiskScoreBreakdownControlTypeContribution,
} from '@risksmart-app/domain/src/types/risk-score-breakdown';

export interface RiskScoreNarrative {
  paragraphs: string[];
}

/**
 * A leaf's breakdown. A parent's `aggregated` breakdown is explained from its
 * children by `buildAggregatedExplanation`; excluding it here means this
 * control-based copy can never render for a child rollup.
 */
export type ControlBasedRiskScoreBreakdown = Exclude<
  RiskScoreBreakdown,
  { status: 'aggregated' }
>;

interface DimensionLabels {
  /** Sentence-start form, e.g. "Likelihood". */
  start: string;
  /** Mid-sentence form, e.g. "likelihood". */
  lower: string;
}

/**
 * Translator-shaped function. Loosely typed so this pure module doesn't
 * import i18next type machinery (which clashes with the project's
 * `returnObjects: true` augmentation when a keyPrefix is in play).
 */
export type NarrativeTranslator = (
  key: string,
  vars?: Record<string, unknown>
) => string;

interface BuildNarrativeOptions {
  /** Translator scoped to the `riskScoreBreakdown.narrative` keyPrefix. */
  t: NarrativeTranslator;
  /**
   * Pre-resolved dimension labels in start-case + lower-case.
   * Example: `{ likelihood: { start: 'Likelihood', lower: 'likelihood' }, ... }`.
   */
  dimensionLabels: {
    likelihood: DimensionLabels;
    impact: DimensionLabels;
  };
}

const INCOMPLETE_KEY: Record<
  Extract<RiskScoreBreakdown, { status: 'incomplete' }>['reason'],
  string
> = {
  missing_inherent_assessment: 'incompleteMissingInherent',
  no_controls_linked: 'incompleteNoControls',
  all_controls_filtered_out: 'incompleteFiltered',
  controls_without_test_results: 'incompleteNoTestResults',
};

const formatPercent = (multiplier: number): number =>
  Math.round((1 - multiplier) * 100);

const dominantContributor = (
  contributions: RiskScoreBreakdownControlTypeContribution[],
  weightField: 'likelihoodWeight' | 'impactWeight'
): RiskScoreBreakdownControlTypeContribution | undefined => {
  const ranked = contributions
    .filter((entry) => entry[weightField] > 0)
    .sort(
      (a, b) =>
        b[weightField] * b.avgEffectiveness -
        a[weightField] * a.avgEffectiveness
    );

  return ranked[0];
};

const formatDimensionSentence = (
  dimensionName: 'likelihood' | 'impact',
  dimension: RiskScoreBreakdownComplete['likelihood'],
  contributions: RiskScoreBreakdownControlTypeContribution[],
  options: BuildNarrativeOptions
): string => {
  const { t, dimensionLabels } = options;
  const labels = dimensionLabels[dimensionName];

  // Edge case 1: no contribution from any type
  if (dimension.band === null && dimension.aggregateAvg === null) {
    return t('noContribution', {
      dimensionStart: labels.start,
      dimensionLower: labels.lower,
    });
  }

  // Edge case 2: zero-effectiveness controls (band 0–1, multiplier 0.95)
  if (dimension.aggregateAvg === 0 && dimension.multiplier === 0.95) {
    return t('ineffective', { dimensionLower: labels.lower });
  }

  // Strength bands
  if (dimension.multiplier >= 0.75) {
    return t('smallReduction', { dimensionLower: labels.lower });
  }

  if (dimension.multiplier <= 0.2) {
    return t('strongReduction', { dimensionLower: labels.lower });
  }

  // Middle bands — surface the dominant contributor with a percentage.
  const weightField =
    dimensionName === 'likelihood' ? 'likelihoodWeight' : 'impactWeight';
  const dominant = dominantContributor(contributions, weightField);
  const reduction = formatPercent(dimension.multiplier);

  if (dominant) {
    return t('midBandWithDominant', {
      count: dominant.count,
      dimensionStart: labels.start,
      percent: reduction,
      type: dominant.type ?? '',
    });
  }

  return t('midBandFallback', {
    dimensionStart: labels.start,
    percent: reduction,
  });
};

const buildCompleteNarrative = (
  breakdown: RiskScoreBreakdownComplete,
  options: BuildNarrativeOptions
): RiskScoreNarrative => {
  const { t } = options;
  const { inherent, residual, controlTypes, likelihood, impact } = breakdown;
  const overallChanged =
    residual.score !== inherent.score ||
    residual.likelihood !== inherent.likelihood ||
    residual.impact !== inherent.impact;

  const intro = overallChanged
    ? t('introChanged', { residual: residual.score, inherent: inherent.score })
    : t('introUnchanged', { score: residual.score });

  return {
    paragraphs: [
      intro,
      formatDimensionSentence('likelihood', likelihood, controlTypes, options),
      formatDimensionSentence('impact', impact, controlTypes, options),
    ],
  };
};

/**
 * Pure formatter — turns a leaf's parsed RiskScoreBreakdown into a structured
 * narrative the popover renders verbatim. Designed to be unit-testable
 * without React.
 */
export const buildNarrative = (
  breakdown: ControlBasedRiskScoreBreakdown,
  options: BuildNarrativeOptions
): RiskScoreNarrative => {
  if (breakdown.status === 'incomplete') {
    return {
      paragraphs: [options.t(INCOMPLETE_KEY[breakdown.reason])],
    };
  }

  return buildCompleteNarrative(breakdown, options);
};
